// Video Storage Utility for Web App
// Handles storing and retrieving video files using IndexedDB

const DB_NAME = 'beevids-videos';
const DB_VERSION = 1;
const STORE_NAME = 'videoFiles';

/**
 * Open IndexedDB database
 */
async function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}

/**
 * Save video file to IndexedDB
 */
export async function saveVideoFile(projectId, file) {
  try {
    console.log('💾 saveVideoFile called:', {
      projectId,
      fileName: file?.name,
      fileSize: file?.size,
      fileType: file?.type,
      isFile: file instanceof File
    });
    
    if (!(file instanceof File)) {
      throw new Error(`Invalid file: not a File object. Got: ${typeof file}, isFile: ${file instanceof File}`);
    }
    
    // Convert File to ArrayBuffer FIRST (before opening transaction)
    console.log('🔄 Converting file to ArrayBuffer...');
    const arrayBuffer = await file.arrayBuffer();
    console.log('✅ ArrayBuffer created:', {
      size: arrayBuffer.byteLength,
      isArrayBuffer: arrayBuffer instanceof ArrayBuffer
    });
    
    // NOW open the transaction after we have the ArrayBuffer
    const db = await openDB();
    console.log('📂 Database opened:', {
      name: db.name,
      version: db.version,
      objectStoreNames: Array.from(db.objectStoreNames)
    });
    
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    console.log('💾 Saving to IndexedDB with key:', projectId);
    await new Promise((resolve, reject) => {
      const request = store.put(arrayBuffer, projectId);
      request.onsuccess = () => {
        console.log('✅ IndexedDB put successful');
        resolve();
      };
      request.onerror = () => {
        console.error('❌ IndexedDB put error:', request.error);
        reject(request.error);
      };
    });
    
    // Wait for transaction to complete before verifying
    await new Promise((resolve, reject) => {
      transaction.oncomplete = () => {
        console.log('✅ Transaction completed successfully');
        resolve();
      };
      transaction.onerror = () => {
        console.error('❌ Transaction error:', transaction.error);
        reject(transaction.error);
      };
      transaction.onabort = () => {
        console.error('❌ Transaction aborted');
        reject(new Error('Transaction was aborted'));
      };
    });
    
    // Verify it was saved (in a new transaction after a short delay)
    // Give IndexedDB a moment to commit the transaction
    await new Promise(resolve => setTimeout(resolve, 100));
    
    console.log('🔍 Verifying save...');
    const verifyDb = await openDB();
    const verifyTransaction = verifyDb.transaction([STORE_NAME], 'readonly');
    const verifyStore = verifyTransaction.objectStore(STORE_NAME);
    const verifyResult = await new Promise((resolve, reject) => {
      const verifyRequest = verifyStore.get(projectId);
      verifyRequest.onsuccess = () => resolve(verifyRequest.result);
      verifyRequest.onerror = () => reject(verifyRequest.error);
    });
    
    if (!verifyResult || !(verifyResult instanceof ArrayBuffer)) {
      console.error('❌ Verification failed:', {
        found: !!verifyResult,
        isArrayBuffer: verifyResult instanceof ArrayBuffer,
        type: typeof verifyResult,
        constructor: verifyResult?.constructor?.name
      });
      throw new Error('Video file was not saved correctly - verification failed');
    }
    console.log('✅ Verification passed:', {
      size: verifyResult.byteLength,
      matches: verifyResult.byteLength === arrayBuffer.byteLength
    });
    
    // Create and return object URL
    const blob = new Blob([arrayBuffer], { type: file.type });
    const url = URL.createObjectURL(blob);
    console.log('✅ Object URL created:', url);
    
    return {
      url,
      name: file.name,
      size: file.size,
      type: file.type
    };
  } catch (error) {
    console.error('❌ Error saving video file:', error);
    throw error;
  }
}

/**
 * Get video file from IndexedDB and create object URL
 */
export async function getVideoFile(projectId) {
  try {
    console.log('🔍 Getting video file from IndexedDB for project:', projectId);
    
    const db = await openDB();
    console.log('📂 Database opened for read:', {
      name: db.name,
      version: db.version,
      objectStoreNames: Array.from(db.objectStoreNames)
    });
    
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    
    const arrayBuffer = await new Promise((resolve, reject) => {
      const request = store.get(projectId);
      request.onsuccess = () => {
        const result = request.result;
        console.log('📦 IndexedDB get result:', {
          found: !!result,
          isArrayBuffer: result instanceof ArrayBuffer,
          isNull: result === null,
          isUndefined: result === undefined,
          size: result ? result.byteLength : 0,
          type: typeof result,
          constructor: result?.constructor?.name
        });
        resolve(result);
      };
      request.onerror = () => {
        console.error('❌ IndexedDB get error:', request.error);
        reject(request.error);
      };
    });
    
    if (!arrayBuffer || !(arrayBuffer instanceof ArrayBuffer)) {
      // List all keys to see what's in IndexedDB
      try {
        // Wait for transaction to complete
        await new Promise((resolve) => {
          transaction.oncomplete = () => resolve();
        });
        
        // Open a new transaction to list keys
        const listDb = await openDB();
        const listTransaction = listDb.transaction([STORE_NAME], 'readonly');
        const listStore = listTransaction.objectStore(STORE_NAME);
        const allKeysRequest = listStore.getAllKeys();
        const allKeys = await new Promise((resolve, reject) => {
          allKeysRequest.onsuccess = () => resolve(allKeysRequest.result);
          allKeysRequest.onerror = () => reject(allKeysRequest.error);
        });
        console.warn('⚠️ No video file found in IndexedDB for project:', projectId, {
          result: arrayBuffer,
          isArrayBuffer: arrayBuffer instanceof ArrayBuffer,
          allKeysInIndexedDB: allKeys,
          lookingFor: projectId,
          found: allKeys.includes(projectId)
        });
      } catch (listError) {
        console.warn('⚠️ No video file found in IndexedDB for project:', projectId, {
          result: arrayBuffer,
          isArrayBuffer: arrayBuffer instanceof ArrayBuffer,
          listError: listError.message
        });
      }
      return null;
    }
    
    // Recreate blob and object URL
    const project = JSON.parse(localStorage.getItem('beevids-projects') || '[]')
      .find(p => p.id === projectId);
    
    const fileType = project?.videoFile?.type || 'video/mp4';
    const fileName = project?.videoFile?.name || 'video.mp4';
    
    console.log('📄 Project video file info:', {
      hasProject: !!project,
      fileType,
      fileName,
      arrayBufferSize: arrayBuffer.byteLength
    });
    
    const blob = new Blob([arrayBuffer], { type: fileType });
    const url = URL.createObjectURL(blob);
    
    console.log('✅ Created object URL:', url);
    
    return {
      url,
      name: fileName,
      size: arrayBuffer.byteLength,
      type: fileType
    };
  } catch (error) {
    console.error('❌ Error getting video file:', error);
    return null;
  }
}

/**
 * Delete video file from IndexedDB
 */
export async function deleteVideoFile(projectId) {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    await new Promise((resolve, reject) => {
      const request = store.delete(projectId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error deleting video file:', error);
  }
}

/**
 * Revoke object URL to free memory
 */
export function revokeVideoUrl(url) {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

/**
 * List all video files in IndexedDB (for debugging)
 */
export async function listAllVideoFiles() {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    
    const keys = await new Promise((resolve, reject) => {
      const request = store.getAllKeys();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    console.log('📋 All video files in IndexedDB:', keys);
    return keys;
  } catch (error) {
    console.error('Error listing video files:', error);
    return [];
  }
}

